

































































































































































































/**
 * FUPOF-193 — look up a Shade Designer configuration code and hand the rep off
 * to the normal umbrella configurator with the design preloaded.
 *
 * Two phases, and the split between them is the point: Submit *resolves* the
 * code and shows what it contains; Review is what navigates. The rep stays on
 * whatever page they opened this from until they have seen the design and
 * confirmed it is the one they meant — "you're not already in the room, you're
 * going: okay, this is the right address, I'm going to go in."
 *
 * An earlier revision navigated on Submit and let the configurator load behind
 * this summary. That saved a page load at the cost of moving the rep for a
 * design they had not agreed to yet, which is wrong for the case this modal
 * exists to serve: a rep holding several codes, checking which is which.
 *
 * The code-entry interaction is ported from Shade Designer's own
 * ConfigCodeModal so reps who use both applications recognise it, with two
 * deliberate departures:
 *
 * 1. It validates before navigating. Shade Designer's submitCode() just
 *    $router.pushes, which makes its own error branches unreachable; here a bad
 *    or expired code has to surface in this modal, because the rep's next screen
 *    would otherwise be a half-built configurator.
 * 2. The reactive-array bug is fixed. The original reassigns codeDigits and then
 *    index-assigns into it (`this.codeDigits[i] = …`), which Vue 2 cannot
 *    observe — it renders correctly only by nextTick luck. Every write here
 *    builds a local array and assigns it once.
 */
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import VueRouter, { RawLocation } from "vue-router";
import { namespace } from "vuex-class";
import ConfigFileService from "@/services/config_file_service";
import { BadRequest, NotFoundError } from "@/services/error_service";
import {
  ResolvedSharedConfig,
  SharedConfigLine
} from "@/models/configurator/shared_config";
import { ProductType } from "@/models/products/product";
import { LineItem } from "@/models/order/order";
import { StoreModule } from "@/store/types";
import { OrderGetters } from "@/store/modules/order/types";
import { sharedDesignCode } from "@/utility/shared_design";

const CODE_LENGTH = 5;

@Component
export default class ConfigCodeModal extends Vue {
  @Prop({ default: false }) show!: boolean;
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrderItems))
  storeItems!: LineItem[];

  protected configFileService = new ConfigFileService();
  protected codeDigits: string[] = new Array(CODE_LENGTH).fill("");
  protected errorMessage = "";
  protected hasError = false;
  protected loading = false;
  protected resolved: ResolvedSharedConfig | null = null;

  @Watch("show", { immediate: true })
  protected onShow(open: boolean): void {
    if (!open) return;
    this.reset();
    this.$nextTick(() => this.focusInput(0));
  }

  protected reset(): void {
    this.codeDigits = new Array(CODE_LENGTH).fill("");
    this.errorMessage = "";
    this.hasError = false;
    this.loading = false;
    this.resolved = null;
  }

  protected close(): void {
    this.$emit("close");
  }

  get isCodeComplete(): boolean {
    return this.codeDigits.every(digit => digit !== "");
  }

  get configCode(): string {
    return this.codeDigits.join("");
  }

  get umbrellaLine(): SharedConfigLine | null {
    return this.lineOfType(ProductType.Umbrella);
  }

  get baseLine(): SharedConfigLine | null {
    return this.lineOfType(ProductType.Base);
  }

  get topCoverLine(): SharedConfigLine | null {
    return this.lineOfType(ProductType.Accessory);
  }

  private lineOfType(type: ProductType): SharedConfigLine | null {
    if (!this.resolved) return null;
    return (
      this.resolved.lines.find(line => line.productType === type) ?? null
    );
  }

  /** True when any line already on the quote came from this same code. */
  get alreadyOnQuote(): boolean {
    if (!this.resolved) return false;
    return this.storeItems.some(
      item => sharedDesignCode(item) === this.resolved!.share_id
    );
  }

  protected focusInput(index: number): void {
    const ref = this.$refs[`codeInput${index}`] as HTMLInputElement[];
    if (ref && ref[0]) ref[0].focus();
  }

  /**
   * Write the whole array in one assignment. `chars` may be shorter than
   * CODE_LENGTH; the remaining slots are cleared.
   */
  private setDigits(chars: string[]): void {
    const next = new Array(CODE_LENGTH).fill("");
    for (let i = 0; i < Math.min(chars.length, CODE_LENGTH); i += 1) {
      next[i] = chars[i];
    }
    this.codeDigits = next;
  }

  private sanitize(value: string): string {
    return value.replace(/[^0-9A-Za-z]/g, "").toUpperCase();
  }

  protected handleInput(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = this.sanitize(target.value);
    this.clearError();

    if (value.length > 1) {
      // A paste that arrives as an input event (some mobile keyboards) — treat
      // it the way handlePaste does and distribute across the boxes.
      this.distribute(value);
      return;
    }

    const next = [...this.codeDigits];
    next[index] = value;
    this.codeDigits = next;
    // The DOM input holds whatever the user typed; force it back in step with
    // the sanitized model (lowercase in, uppercase out).
    target.value = value;

    if (value && index < CODE_LENGTH - 1) {
      this.$nextTick(() => this.focusInput(index + 1));
    }
  }

  private distribute(value: string): void {
    const chars = value.split("");
    this.setDigits(chars);
    const filled = Math.min(chars.length, CODE_LENGTH);
    this.$nextTick(() =>
      this.focusInput(filled < CODE_LENGTH ? filled : CODE_LENGTH - 1)
    );
  }

  protected handleKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === "Backspace" && !this.codeDigits[index] && index > 0) {
      this.focusInput(index - 1);
    }
    if (event.key === "Enter" && this.isCodeComplete && !this.loading) {
      this.lookUp();
    }
  }

  protected handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = this.sanitize(
      event.clipboardData?.getData("text") ?? ""
    );
    if (!pasted) return;
    this.clearError();
    this.distribute(pasted);
  }

  protected clearError(): void {
    this.errorMessage = "";
    this.hasError = false;
  }

  protected showError(message: string): void {
    this.errorMessage = message;
    this.hasError = true;
  }

  protected async lookUp(): Promise<void> {
    if (!this.isCodeComplete) {
      this.showError("Please enter all 5 characters of the code.");
      return;
    }
    this.loading = true;
    this.clearError();
    try {
      const resolved = await this.configFileService.resolveShareCode(
        this.configCode
      );
      const umbrella = resolved.lines.find(
        line => line.productType === ProductType.Umbrella
      );
      // Without a product_line the configurator's getItems() comes back empty
      // and its template throws on an undefined selection — refuse here rather
      // than navigate into a page that cannot render.
      if (!umbrella || !umbrella.product_line) {
        this.showError(
          "This design's product line is no longer available."
        );
        return;
      }
      // Resolve and stop. The rep stays on whatever page they opened this
      // from until they press Review — submitting a code is a lookup, not a
      // commitment, and a rep checking which of several codes is the right one
      // should not be moved off their page to find out.
      this.resolved = resolved;
    } catch (err) {
      if (err instanceof NotFoundError || err instanceof BadRequest) {
        this.showError(err.message);
      } else {
        this.showError("Something went wrong, please try again.");
      }
    } finally {
      this.loading = false;
    }
  }

  /**
   * Phase B's confirm, and the only thing in this modal that navigates.
   * Nothing is added to the quote by going there — lines are created when the
   * rep adds the umbrella, not when the configurator loads.
   */
  protected async review(): Promise<void> {
    const umbrella = this.umbrellaLine;
    // lookUp() refuses to resolve without one, so this cannot be hit from the
    // UI; it keeps the non-null assertions below honest.
    if (!umbrella || !umbrella.product_line || !this.resolved) return;
    const location: RawLocation = {
      name: "ItemCustomization",
      params: { product: ProductType.Umbrella },
      query: {
        handle: umbrella.product_line as string,
        share: this.resolved.share_id
      }
    };
    // Close first so the configurator loads on a clear screen rather than
    // behind this summary. App.vue v-ifs the modal away on `close`, which
    // destroys this component, so the router is captured beforehand instead of
    // being reached through `this` afterwards.
    const router = this.$router;
    this.close();
    await this.navigate(location, router);
  }

  /**
   * Dismissal, from the header X or the backdrop, in either phase. It closes
   * and nothing else: the rep never left the page they opened this from, so
   * there is nowhere to send them back to and no line to undo.
   */
  protected cancel(): void {
    this.close();
  }

  /**
   * vue-router 3 rejects a push to the location you are already on. That is a
   * no-op here (re-entering the same code from the same design's page), not a
   * failure, so it is swallowed rather than surfaced as an error.
   */
  private async navigate(
    location: RawLocation,
    router: VueRouter = this.$router
  ): Promise<void> {
    try {
      await router.push(location);
    } catch (err) {
      if ((err as Error)?.name !== "NavigationDuplicated") throw err;
    }
  }
}
